import { IconButton } from '@ads/components-react';
import { InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { BANNER_PREVIEW_LABEL_VALUES } from '../../../utils/bannerMedia';
import { BannerPreviewFrame } from '../BannerPreviewFrame';

export interface BannerPreviewDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly imageUrl: string | undefined;
  readonly videoUrl: string | undefined;
  readonly overlayTitle: string | undefined;
}

/**
 * Modal de preview do banner em tela cheia: a mídia ocupa toda a largura do modal,
 * com altura natural, para o operador conferir a arte **inteira** — sem o recorte
 * que o card do dashboard aplica.
 *
 * Construído sobre o `<dialog>` nativo, pelo mesmo motivo do `ConfirmDialog`
 * compartilhado: o modo modal do Radix marca o `body` com `pointer-events: none`
 * e deixa o estilo preso quando o diálogo é aberto de dentro de outra camada ou
 * desmontado aberto. Com `showModal()` o browser cuida de foco preso, `Esc` e
 * backdrop — e o clique fora chega como clique no próprio `<dialog>`.
 */
export function BannerPreviewDialog({
  open,
  onClose,
  imageUrl,
  videoUrl,
  overlayTitle,
}: BannerPreviewDialogProps) {
  const { t } = useTranslation('banner');
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: o clique aqui só detecta o backdrop; Esc é nativo do <dialog>
    <dialog
      ref={ref}
      aria-labelledby="banner-preview-dialog-title"
      className="m-auto w-[40rem] max-lg:w-full rounded-md bg-ctx-layout-surface p-none text-ctx-content-base shadow-level3 backdrop:bg-base-gray-900/intense"
      onCancel={event => {
        event.preventDefault();
        onClose();
      }}
      onClick={event => {
        if (event.target === ref.current) onClose();
      }}
    >
      {/* O conteúdo só existe enquanto o modal está aberto: mantê-lo montado
          duplicaria a mídia (dois frames a mais carregando o mesmo arquivo) e
          faria o mesmo texto aparecer duas vezes na árvore acessível. */}
      {open ? (
        <div className="flex flex-col gap-xxs">
          <div className="flex items-center justify-between gap-mini border-b-hairline border-b-ctx-layout-body py-micro px-xxs">
            <div className="flex items-center gap-quark">
              <h2 id="banner-preview-dialog-title" className="ts-heading-xs text-ctx-content-title">
                {t('banner.form.preview.dialog.title')}
              </h2>

              {/* Tooltip inline em vez do `Tooltip` do ADS: aquele é portado para o
                  `body`, que fica **atrás** do `<dialog>` nativo (top layer) — a
                  explicação simplesmente não apareceria. */}
              <span className="group relative flex">
                <button
                  type="button"
                  className="peer flex rounded-circular text-ctx-content-base transition-colors hover:text-ctx-content-title focus-visible:text-ctx-content-title"
                  aria-describedby="banner-preview-dialog-note"
                  aria-label={t('banner.form.preview.dialog.noteLabel')}
                >
                  <InformationCircleIcon className="size-xxs shrink-0" />
                </button>

                {/* `z-[60]` porque o quadro da mídia vem depois no DOM e, sem camada
                    própria, pintava por cima do texto (a explicação aparecia cortada na
                    borda da arte). Com eventos de ponteiro ligados, o operador pode
                    passar o mouse por cima para ler até o fim, e selecionar o texto.

                    O gatilho de teclado é `peer-focus-visible` (não `focus-within`):
                    ao abrir, o `<dialog>` manda o foco para o primeiro elemento focável
                    — este ícone — e o tooltip abria sozinho em toda abertura do modal. */}
                <span
                  id="banner-preview-dialog-note"
                  role="tooltip"
                  className="absolute top-full left-none z-[60] mt-nano hidden w-[24rem] max-w-[calc(100vw-4rem)] rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-spotlight px-micro py-nano ts-caption-xxs text-ctx-content-base shadow-level4 group-hover:block peer-focus-visible:block"
                >
                  {t('banner.form.preview.dialog.note', BANNER_PREVIEW_LABEL_VALUES)}
                </span>
              </span>
            </div>

            <IconButton
              icon={XMarkIcon}
              hierarchy="tertiary"
              size="sm"
              ariaLabel={t('banner.form.preview.dialog.close')}
              onClick={onClose}
            />
          </div>
          <div className="px-xs !pb-xxs">
            <BannerPreviewFrame
              imageUrl={imageUrl}
              videoUrl={videoUrl}
              overlayTitle={overlayTitle}
              variant="modal"
            />
          </div>
        </div>
      ) : null}
    </dialog>
  );
}
